<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("dmp.outputInformation.title") }}</h5>
              <div class="d-flex gap-1">
                <b-button variant="primary" size="sm" @click="addNewOutput" :disabled="isLoading"
                  class="text-capitalize">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.outputInformation.addOutput") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-4">
              <b-spinner variant="primary" :label="$t('dmp.outputInformation.loadingOutputs')"></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.outputInformation.loadingOutputs") }}</p>
            </div>

            <b-row v-if="!isLoading">
              <b-col cols="7" class="table-column">
                <!-- Outputs Table -->
                <b-table :items="outputs" :fields="outputTableFields" responsive striped hover show-empty
                  :empty-text="$t('dmp.outputInformation.noOutputsConfigured')" selectable select-mode="single"
                  @row-selected="onRowSelected" sort-by="id" :sort-desc="false" class="mb-0">
                  <template #cell(id)="data">
                    {{ data.value }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.value }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="5" class="details-column">
                <!-- Selected Output Details Card -->
                <b-card v-if="selectedOutput" no-body>
                  <b-card-header class="mb-1 card-header-custom pt-0">
                    <h6 class="mb-0">{{ $t("dmp.outputInformation.outputDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="outputFormObserver">
                      <b-form @submit.prevent="saveOutput">
                        <b-row>
                          <b-col cols="12">
                            <validation-provider #default="validationContext"
                              :name="$t('dmp.outputInformation.outputNumber')" :rules="{ required: true }">
                              <b-form-group :label="$t('dmp.outputInformation.outputNumber')" label-for="outputNumber">
                                <b-form-input id="outputNumber" v-model="selectedOutput.outputNumber" type="number"
                                  :state="getValidationState(validationContext)"
                                  :placeholder="$t('dmp.outputInformation.outputNumber')" :disabled="isEditingOutput" />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12">
                            <validation-provider #default="validationContext"
                              :name="$t('dmp.outputInformation.outputName')" :rules="{ required: true, max: 50 }">
                              <b-form-group :label="$t('dmp.outputInformation.outputName')" label-for="outputName">
                                <b-form-input id="outputName" v-model="selectedOutput.name" type="text"
                                  :state="getValidationState(validationContext)"
                                  :placeholder="$t('dmp.outputInformation.outputName')" maxlength="32" />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12">
                            <validation-provider #default="validationContext"
                              :name="$t('dmp.outputInformation.description')" :rules="{ max: 200 }">
                              <b-form-group :label="$t('dmp.outputInformation.description')"
                                label-for="outputDescription">
                                <b-form-textarea id="outputDescription" v-model="selectedOutput.description"
                                  :state="getValidationState(validationContext)"
                                  :placeholder="$t('dmp.outputInformation.description')" maxlength="200" rows="2" />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12">
                            <b-form-group>
                              <b-form-checkbox id="realTimeStatus" v-model="selectedOutput.realTimeStatus" value="Y"
                                unchecked-value="N">
                                Real-time Status
                              </b-form-checkbox>
                            </b-form-group>
                          </b-col>

                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-2">
                            <b-button v-if="isEditingOutput" variant="outline-danger" size="sm" class="mr-1"
                              @click="deleteOutput" :disabled="isSaving || isDeleting">
                              <b-spinner v-if="isDeleting" small class="mr-25" />
                              <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button variant="primary" size="sm" type="submit" :disabled="isSaving || isDeleting">
                              <b-spinner v-if="isSaving" small class="mr-25" />
                              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                              <span>{{
                                isSaving ? $t("actions.saving") : $t("actions.save")
                              }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import { useTemplateMode } from "./useTemplateMode";

// Custom validation for serial number range
extend("serial_number_range", {
  validate: (value) => {
    const numValue = parseInt(value, 10);
    return numValue >= 15100000 && numValue <= 15299999;
  },
  message: "The serial number must be between 15100000 and 15299999.",
});

export default {
  name: "ConfigOutputInformation",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    outputInformationData: {
      type: Array,
      default: () => [],
    },
    outputInformationConfig: {
      type: Object,
      default: () => { },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    const outputs = ref([]);
    const isLoading = computed(() => props.loading);
    const isSaving = ref(false);
    const isDeleting = ref(false);

    const outputFormObserver = ref(null);
    const isEditingOutput = ref(false);
    const editingOutputIndex = ref(-1);
    const selectedOutput = ref(null);

    const outputTableFields = computed(() => [
      {
        key: "outputNumber",
        label: proxy.$t("dmp.outputInformation.outputNumber"),
        sortable: true,
      },
      {
        key: "outputName",
        label: proxy.$t("dmp.outputInformation.outputName"),
        sortable: true,
      },
    ]);

    const supervisionTimeOptions = [
      { value: 0, text: "None" },
      { value: 1, text: "1 second" },
      { value: 2, text: "2 seconds" },
      { value: 3, text: "3 seconds" },
    ];

    const loadOutputs = () => {
      if (!props.outputInformationData) {
        outputs.value = [];
        return;
      }

      try {

        outputs.value = props.outputInformationData.map((item) => {
          return {
            outputNumber: parseInt(item.outputNumber),
            outputName: item.outputName,
            description: item.description,
            serialNumber: item.serialNumber,
            supervisionTime: item.supervisionTime,
            tripWithPanelBell: item.tripWithPanelBell,
            realTimeStatus: item.realTimeStatus,
          }
        })

      } catch (error) {
        console.error("Error loading outputs:", error);
        outputs.value = [];
      }
    };

    const addNewOutput = () => {
      if (outputs.value.length >= 999) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.outputInformation.messages.limitReached"),
            text: proxy.$t("dmp.outputInformation.messages.maxOutputsAllowed"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      isEditingOutput.value = false;
      editingOutputIndex.value = -1;
      selectedOutput.value = {
        outputNumber: "",
        name: "",
        description: "",
        serialNumber: "",
        supervisionTime: 0,
        tripWithPanelBell: "N",
        realTimeStatus: "Y",
      };
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];

        let description = selected.description || "";
        if (isTemplateMode.value && props.outputInformationConfig.output_information?.length) {
          const configItem = props.outputInformationConfig.output_information.find(
            (c) => parseInt(c.id) === parseInt(selected.outputNumber)
          );
          description = configItem ? (configItem.description || "") : "";
        }

        isEditingOutput.value = true;
        editingOutputIndex.value = outputs.value.findIndex((o) => o.id === selected.id);
        selectedOutput.value = {
          outputNumber: parseInt(selected.outputNumber),
          name: selected.outputName,
          description,
          serialNumber: "",
          supervisionTime: 0,
          tripWithPanelBell: "N",
          realTimeStatus: selected.realTimeStatus || "Y",
        };
      } else {
        selectedOutput.value = null;
        isEditingOutput.value = false;
      }
    };

    const deleteOutput = async () => {
      if (!isEditingOutput.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const list = (props.outputInformationData || []).filter(
          (o) => parseInt(o.outputNumber) !== parseInt(selectedOutput.value.outputNumber)
        );

        const configList = (props.outputInformationConfig.output_information || []).filter(
          (c) => parseInt(c.id) !== parseInt(selectedOutput.value.outputNumber)
        );

        emit("template-update", {
          output_information: list,
          config: { ...props.outputInformationConfig, output_information: configList },
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.outputInformation.messages.outputDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedOutput.value = null;
        isEditingOutput.value = false;
        return;
      }

      isDeleting.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const outputToDelete = selectedOutput.value;

        const response = await store.dispatch("dmp_panels/deleteOutput", {
          uuid: props.uuid,
          outputNumber: outputToDelete.outputNumber,
        });

        if (response.success) {
          emit("operation-completed");
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.outputInformation.messages.outputDeleteSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedOutput.value = null;
          isEditingOutput.value = false;
        } else {
          throw new Error(response.message || "Failed to delete output");
        }
      } catch (error) {
        console.error("Error deleting output:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.outputInformation.messages.outputDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);

        isDeleting.value = false;
      }
    };

    const saveOutput = async (event) => {
      const isValid = await outputFormObserver.value.validate();
      if (!isValid) {
        return;
      }

      if (isTemplateMode.value) {
        const so = selectedOutput.value;
        const list = [...(props.outputInformationData || [])];
        const idx = list.findIndex(
          (o) => parseInt(o.outputNumber) === parseInt(so.outputNumber)
        );
        // Spread the original GET item so serialNumber / supervisionTime /
        // tripWithPanelBell (not faithfully loaded by the form) keep their GET
        // values. Only name / number / realTimeStatus are form-driven.
        const base =
          idx !== -1
            ? list[idx]
            : {
              serialNumber: "--------",
              supervisionTime: "-",
              tripWithPanelBell: "-",
              realTimeStatus: "Y",
            };
        const item = {
          ...base,
          outputName: (so.name || "").toUpperCase(),
          outputNumber: String(parseInt(so.outputNumber)).padStart(3, "0"),
          realTimeStatus: so.realTimeStatus || base.realTimeStatus || "Y",
        };
        if (idx !== -1) {
          item.id = base.id != null ? base.id : nextSequentialId(list);
          list.splice(idx, 1, item);
        } else {
          item.id = nextSequentialId(list);
          list.push(item);
        }

        const configList = [...(props.outputInformationConfig.output_information || [])];
        const configIdx = configList.findIndex(
          (c) => parseInt(c.id) === parseInt(so.outputNumber)
        );
        const configItem = {
          id: parseInt(so.outputNumber),
          description: so.description || "",
        };
        if (configIdx !== -1) {
          configList.splice(configIdx, 1, configItem);
        } else {
          configList.push(configItem);
        }

        emit("template-update", {
          output_information: list,
          config: { ...props.outputInformationConfig, output_information: configList },
        });
        toast({
          component: ToastificationContent,
          props: {
            title: isEditingOutput.value
              ? proxy.$t("dmp.outputInformation.messages.outputUpdatedSuccess")
              : proxy.$t("dmp.outputInformation.messages.outputAddedSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedOutput.value = null;
        isEditingOutput.value = false;
        return;
      }

      const outputToSave = {
        outputNumber: parseInt(selectedOutput.value.outputNumber),
        serialNumber: selectedOutput.value.serialNumber,
        supervisionTime: parseInt(selectedOutput.value.supervisionTime),
        tripWithPanelBell: selectedOutput.value.tripWithPanelBell,
        realTimeStatus: selectedOutput.value.realTimeStatus,
        name: selectedOutput.value.name.toUpperCase(),
        description: selectedOutput.value.description || "",
      };

      isSaving.value = true;
      store.commit("dmp_panels/setOperationActive", true);
      try {
        const response = await store.dispatch("dmp_panels/setOutput", {
          uuid: props.uuid,
          outputData: outputToSave,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: isEditingOutput.value
                ? proxy.$t("dmp.outputInformation.messages.outputUpdatedSuccess")
                : proxy.$t("dmp.outputInformation.messages.outputAddedSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedOutput.value = null;
          isEditingOutput.value = false;
        } else {
          throw new Error(response.message || "Failed to save output");
        }
      } catch (error) {
        console.error("Error saving output:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.outputInformation.messages.outputSaveError"),
            text:
              error.message || proxy.$t("dmp.outputInformation.messages.outputSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSaving.value = false;
      }
    };

    watch(
      () => props.outputInformationData,
      (newData) => {
        if (newData) {
          outputs.value = [...newData];
        }
      },
      { deep: true }
    );

    onMounted(() => {
      loadOutputs();
    });

    return {
      outputs,
      isLoading,
      isSaving,
      isDeleting,
      selectedOutput,
      isEditingOutput,
      editingOutputIndex,
      outputFormObserver,
      outputTableFields,
      supervisionTimeOptions,

      addNewOutput,
      onRowSelected,
      deleteOutput,
      saveOutput,
      loadOutputs,

      refFormObserver,
      getValidationState,

      t,
      toast,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
